const FILE_HEADER_SIZE = 14
const DIB_HEADER_SIZE = 40
const PIXEL_DATA_OFFSET = FILE_HEADER_SIZE + DIB_HEADER_SIZE

/**
 * Encode a canvas as a 24-bit uncompressed BMP (BITMAPFILEHEADER + BITMAPINFOHEADER,
 * bottom-up BGR rows padded to a 4-byte boundary). No library — BMP is simple enough
 * to write by hand. Alpha is dropped; callers should flatten transparency first
 * (BMP has no alpha channel).
 */
export const encodeBmp = (canvas: HTMLCanvasElement): Blob => {
  const {width, height} = canvas
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  const {data} = ctx.getImageData(0, 0, width, height)

  const rowSize = Math.ceil((width * 3) / 4) * 4
  const imageSize = rowSize * height
  const fileSize = PIXEL_DATA_OFFSET + imageSize

  const buffer = new Uint8Array(fileSize)
  const view = new DataView(buffer.buffer)

  // BITMAPFILEHEADER
  buffer[0] = 0x42 // 'B'
  buffer[1] = 0x4d // 'M'
  view.setUint32(2, fileSize, true)
  view.setUint32(6, 0, true) // reserved
  view.setUint32(10, PIXEL_DATA_OFFSET, true)

  // BITMAPINFOHEADER
  view.setUint32(14, DIB_HEADER_SIZE, true)
  view.setInt32(18, width, true)
  view.setInt32(22, height, true) // positive height = bottom-up
  view.setUint16(26, 1, true) // planes
  view.setUint16(28, 24, true) // bit count
  view.setUint32(30, 0, true) // compression = BI_RGB
  view.setUint32(34, imageSize, true)
  view.setInt32(38, 2835, true) // ~72 DPI
  view.setInt32(42, 2835, true)
  view.setUint32(46, 0, true) // colors used
  view.setUint32(50, 0, true) // colors important

  for (let row = 0; row < height; row++) {
    const srcY = height - 1 - row
    const rowOffset = PIXEL_DATA_OFFSET + row * rowSize
    for (let x = 0; x < width; x++) {
      const srcIdx = (srcY * width + x) * 4
      const dstIdx = rowOffset + x * 3
      buffer[dstIdx] = data[srcIdx + 2] // B
      buffer[dstIdx + 1] = data[srcIdx + 1] // G
      buffer[dstIdx + 2] = data[srcIdx] // R
    }
  }

  return new Blob([buffer], {type: 'image/bmp'})
}
